// Sound effects and ambience. Names and variant counts come from /sfx/sfx.json (tools/build-sfx.py).
// Effects with a position get quieter with distance from the camera's target.
import Phaser from 'phaser';
import { Settings } from './settings.ts';

const MUTE_KEY = 'reclaim-muted';
const AMBIENCE = 0.45; // ambience loudness at 100%

export class Sfx {
  private counts: Record<string, number> = {};
  private ambient: Phaser.Sound.BaseSound | null = null;
  private ambientName = '';
  muted = localStorage.getItem(MUTE_KEY) === '1';
  listener = { x: 0, y: 0 };

  constructor(private scene: Phaser.Scene) {}

  /** Call in preload. */
  preload(): void {
    this.scene.load.json('sfx-meta', '/sfx/sfx.json');
  }

  /** Call in create: queues the audio files and starts the loader. */
  load(): void {
    this.counts = (this.scene.cache.json.get('sfx-meta') as Record<string, number>) ?? {};
    for (const [name, n] of Object.entries(this.counts)) for (let i = 0; i < n; i++) this.scene.load.audio(`sfx-${name}-${i}`, `/sfx/${name}_${i}.mp3`);
    this.scene.load.start();
    this.scene.sound.mute = this.muted;
    // Volumes are applied per sound (master x effects, master x ambience), so they hold even
    // before the browser lets audio start.
    Settings.watch((s) => {
      const amb = this.ambient as (Phaser.Sound.BaseSound & { volume: number }) | null;
      if (amb && !this.scene.tweens.isTweening(amb)) amb.volume = AMBIENCE * s.master * s.ambience;
    });
  }

  play(name: string, at?: { x: number; y: number }, volume = 1): void {
    const n = this.counts[name];
    if (!n || this.muted) return;
    let vol = volume * Settings.get().master * Settings.get().effects;
    if (at) {
      const d = Math.hypot(at.x - this.listener.x, at.y - this.listener.y);
      if (d > 900) return;
      vol *= Math.max(0, 1 - d / 900);
    }
    const key = `sfx-${name}-${Math.floor(Math.random() * n)}`;
    if (!this.scene.cache.audio.exists(key)) return;
    this.scene.sound.play(key, { volume: vol, detune: (Math.random() - 0.5) * 120 });
  }

  /** Loops one ambience track, crossfading when it changes. */
  ambience(name: string): void {
    if (name === this.ambientName) return;
    const key = `sfx-${name}-0`;
    if (!this.scene.cache.audio.exists(key)) return;
    this.ambientName = name;
    const old = this.ambient;
    if (old) this.scene.tweens.add({ targets: old, volume: 0, duration: 2500, onComplete: () => old.destroy() });
    const next = this.scene.sound.add(key, { loop: true, volume: 0 });
    next.play();
    this.scene.tweens.add({ targets: next, volume: AMBIENCE * Settings.get().master * Settings.get().ambience, duration: 2500 });
    this.ambient = next;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    this.scene.sound.mute = this.muted;
    localStorage.setItem(MUTE_KEY, this.muted ? '1' : '0');
    return this.muted;
  }
}
